/**
 * Utilitário para normalização de números escritos por extenso e numerais em exercícios de pronúncia.
 * Suporta Francês, Inglês, Italiano, Espanhol e Português.
 */

// Mapeamento de numerais para palavras por idioma
const NUMBER_WORDS: Record<string, Record<string, string>> = {
  fr: {
    '0': 'zero', '1': 'un', '2': 'deux', '3': 'trois', '4': 'quatre', '5': 'cinq',
    '6': 'six', '7': 'sept', '8': 'huit', '9': 'neuf', '10': 'dix',
    '11': 'onze', '12': 'douze', '13': 'treize', '14': 'quatorze', '15': 'quinze',
    '16': 'seize', '17': 'dix-sept', '18': 'dix-huit', '19': 'dix-neuf', '20': 'vingt',
    '21': 'vingt-et-un', '30': 'trente', '40': 'quarante', '50': 'cinquante',
    '60': 'soixante', '70': 'soixante-dix', '80': 'quatre-vingts', '90': 'quatre-vingt-dix', '100': 'cent'
  },
  en: {
    '0': 'zero', '1': 'one', '2': 'two', '3': 'three', '4': 'four', '5': 'five',
    '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine', '10': 'ten',
    '11': 'eleven', '12': 'twelve', '13': 'thirteen', '14': 'fourteen', '15': 'fifteen',
    '16': 'sixteen', '17': 'seventeen', '18': 'eighteen', '19': 'nineteen', '20': 'twenty',
    '21': 'twenty-one', '30': 'thirty', '40': 'forty', '50': 'fifty',
    '60': 'sixty', '70': 'seventy', '80': 'eighty', '90': 'ninety', '100': 'hundred'
  },
  it: {
    '0': 'zero', '1': 'uno', '2': 'due', '3': 'tre', '4': 'quattro', '5': 'cinque',
    '6': 'sei', '7': 'sette', '8': 'otto', '9': 'nove', '10': 'dieci',
    '11': 'undici', '12': 'dodici', '13': 'tredici', '14': 'quattordici', '15': 'quindici',
    '16': 'sedici', '17': 'diciassette', '18': 'diciotto', '19': 'diciannove', '20': 'venti',
    '21': 'ventuno', '30': 'trenta', '40': 'quaranta', '50': 'cinquanta',
    '60': 'sessanta', '70': 'settanta', '80': 'ottanta', '90': 'novanta', '100': 'cento'
  },
  es: {
    '0': 'cero', '1': 'uno', '2': 'dos', '3': 'tres', '4': 'cuatro', '5': 'cinco',
    '6': 'seis', '7': 'siete', '8': 'ocho', '9': 'nueve', '10': 'diez',
    '11': 'once', '12': 'doce', '13': 'trece', '14': 'catorce', '15': 'quince',
    '16': 'dieciséis', '17': 'diecisiete', '18': 'dieciocho', '19': 'diecinueve', '20': 'veinte',
    '21': 'veintiuno', '30': 'treinta', '40': 'cuarenta', '50': 'cincuenta',
    '60': 'sesenta', '70': 'setenta', '80': 'ochenta', '90': 'noventa', '100': 'cien'
  },
  pt: {
    '0': 'zero', '1': 'um', '2': 'dois', '3': 'tres', '4': 'quatro', '5': 'cinco',
    '6': 'seis', '7': 'sete', '8': 'oito', '9': 'nove', '10': 'dez',
    '11': 'onze', '12': 'doze', '13': 'treze', '14': 'quatorze', '15': 'quinze',
    '16': 'dezesseis', '17': 'dezessete', '18': 'dezoito', '19': 'dezenove', '20': 'vinte',
    '21': 'vinte e um', '30': 'trinta', '40': 'quarenta', '50': 'cinquenta',
    '60': 'sessenta', '70': 'setenta', '80': 'oitenta', '90': 'noventa', '100': 'cem'
  }
};

// Mapeamento reverso (palavra -> numeral)
const WORD_TO_NUMBER: Record<string, Record<string, string>> = {};
for (const [lang, map] of Object.entries(NUMBER_WORDS)) {
  WORD_TO_NUMBER[lang] = {};
  for (const [digit, word] of Object.entries(map)) {
    // Normalizar a chave da palavra (remover acentos e hífens)
    const cleanWord = word.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/-/g, ' ').toLowerCase();
    WORD_TO_NUMBER[lang][cleanWord] = digit;
    // Também guardar a versão com hífen se houver
    if (word.includes('-')) {
      WORD_TO_NUMBER[lang][word.toLowerCase()] = digit;
    }
  }
}

/**
 * Normaliza qualquer ocorrência de número na string para numerais comuns (ex: 'vingt' -> '20', 'twenty' -> '20').
 * Se um dos lados tiver numeral ou palavra, ambos serão convertidos para o mesmo padrão.
 */
export function normalizeNumbersInText(text: string, langCode: string = 'en'): string {
  if (!text) return '';
  const cleanLang = langCode.toLowerCase().split('-')[0];
  const dict = WORD_TO_NUMBER[cleanLang] || WORD_TO_NUMBER['en'] || {};

  let result = text.toLowerCase();

  // Substituir palavras numéricas pelo dígito (ordem decrescente de tamanho para palavras compostas)
  const sortedWords = Object.keys(dict).sort((a, b) => b.length - a.length);
  for (const word of sortedWords) {
    const digit = dict[word];
    // Regex com limites de palavra ou pontuação
    const regex = new RegExp(`(^|\\s|[.,\/#!$%\^&\*;:{}=\\-_~()?"])${word}($|\\s|[.,\/#!$%\^&\*;:{}=\\-_~()?"])`, 'gi');
    result = result.replace(regex, `$1${digit}$2`);
  }

  return result;
}
